import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { Loader2, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { SplitReviewLayout, ReviewFormPane, ReviewStatusChip, TOOLBAR_BUTTON } from "@/components/shared/SplitReviewLayout";
import { buildSaveStatus, getFirstErrorPath } from "@/utils/reviewFormStatus";
import { buildReviewSchema } from "@/pages/user/confirm-details/validation.schema";
import { mapApiToForm } from "@/pages/user/confirm-details/utils/mapApiToForm";
import StepPersonal from "@/pages/user/confirm-details/components/steps/StepPersonal";
import StepSkills from "@/pages/user/confirm-details/components/steps/StepSkills";
import StepExperience from "@/pages/user/confirm-details/components/steps/StepExperience";
import StepEducation from "@/pages/user/confirm-details/components/steps/StepEducation";
import StepSalary from "@/pages/user/confirm-details/components/steps/StepSalary";
import StepMoreInfo from "@/pages/user/confirm-details/components/steps/StepMoreInfo";
import LanguageProficiencyRows from "@/components/shared/LanguageProficiencyRows";
import ResumePreview from "@/pages/admin/candidate-analysis/edit-profile/components/ResumePreview";
import { CandidateService } from "@/services/candidateService";
import { JobService } from "@/services/jobService";
import { getDefaultCurrencyFallback } from "@/hooks/useDefaultCurrency";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { requiredLanguageNames } from "@/utils/languageRequirements";
import { websiteValue } from "@/utils/website";

const SECTIONS = [
  { key: "personal", label: "Personal Info" },
  { key: "skills", label: "Skills & Language" },
  { key: "experience", label: "Experience" },
  { key: "education", label: "Education" },
  { key: "salary", label: "Salary & Availability" },
  { key: "moreinfo", label: "More Info" },
];

const FIELD_TO_SECTION = {
  personal_information: "personal",
  skills: "skills",
  experience: "experience",
  education: "education",
  compensation: "salary",
  additionalSections: "moreinfo",
};

// `JobFitApplyRequest` mirrors `CandidateConfirmRequest` — current_salary,
// expected_salary and total_experience are validated server-side as STRINGS
// (SALARY_REGEX / EXPERIENCE_REGEX), not numbers. Sending a JS number here
// fails Pydantic with "Input should be a valid string" before the regex ever
// runs.
const emptyToNull = (val) => (val === "" || val === null || val === undefined ? null : val);

/**
 * The review-and-submit route for "Apply from Suitable Jobs".
 *
 * Reuses the confirm-details step components and validation schema — this is
 * the same review-and-submit action the candidate-facing flow uses, just
 * recruiter-initiated and prefilled from an already-confirmed extraction
 * instead of a fresh one. Follows the same `useForm` + `zodResolver` shape as
 * the other admin-side review screens (edit-profile, reupload-resume), not
 * confirm-details' own step-wizard hook — there is no wizard here, no upload
 * stage, and no LLM call: the prefill is a plain read.
 *
 * Reachable only by way of the Suitable Jobs tab's attestation dialog, which
 * stamps `location.state.attested = true` before navigating here — a direct
 * URL, bookmark, or reload has no such state and is sent back to re-attest.
 */
const ApplyToJob = () => {
  const { candidateId, jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sectionRefs = useRef({});
  const setHeader = useOutletContext();

  const attested = location.state?.attested === true;
  const fromPath = location.state?.fromPath || `/admin/candidate-analysis/${candidateId}`;
  const jobTitleFromState = location.state?.jobTitle;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [mandatoryFields, setMandatoryFields] = useState([]);
  // The job's own language rows — prefilled first and locked in the editor,
  // same as the candidate-facing apply flow (see `languagesSlot` below).
  const [jobRequiredLanguages, setJobRequiredLanguages] = useState([]);
  const [jobTitle, setJobTitle] = useState(jobTitleFromState || "");
  const [sourceApplicationId, setSourceApplicationId] = useState(null);
  const [currencySign, setCurrencySign] = useState(getDefaultCurrencyFallback().currencySign);
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [highlightedSection, setHighlightedSection] = useState(null);

  const schema = useMemo(() => buildReviewSchema(mandatoryFields), [mandatoryFields]);

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const watchedValues = methods.watch();
  const saveStatus = useMemo(() => buildSaveStatus(watchedValues, mandatoryFields, methods.formState.errors), [watchedValues, mandatoryFields, methods.formState.errors]);

  useEffect(() => {
    setHeader({
      title: "Apply to Job",
      subTitle: jobTitle ? `Applying to ${jobTitle}` : "Review candidate details",
    });

    return () => {
      setHeader({
        title: "",
        subTitle: "",
      });
    };
  }, [setHeader, jobTitle]);
  useEffect(() => {
    if (!attested) return; // redirected below; nothing to load
    if (!candidateId || !jobId) return;

    let cancelled = false;
    setLoading(true);
    setLoadError("");

    (async () => {
      try {
        const [fieldConfigRes, prefill, jobRes] = await Promise.all([
          JobService.fetchJobFieldConfig(jobId),
          CandidateService.fetchJobFitApplyPrefill(candidateId, jobId),
          JobService.fetchJobById(jobId).catch(() => null),
        ]);
        if (cancelled) return;

        if (fieldConfigRes?.success && fieldConfigRes?.data) {
          const allMandatory = [...(fieldConfigRes.data.always_mandatory || []).map((f) => f.key), ...(fieldConfigRes.data.configured_mandatory || []).map((f) => f.key)];
          setMandatoryFields(allMandatory);
        }

        const requiredLanguages = jobRes?.data?.languages_required || [];
        setJobRequiredLanguages(requiredLanguages);
        setJobTitle(prefill?.job_title || jobTitleFromState || "");
        setSourceApplicationId(prefill?.source_application_id || null);
        setCurrencySign(prefill?.currency_sign || getDefaultCurrencyFallback().currencySign);
        // `mapApiToForm` (shared with confirm-details) reads its required
        // rows off `job_required_languages` — this prefill endpoint doesn't
        // carry that key, so it rides in here instead.
        methods.reset(mapApiToForm({ ...prefill, job_required_languages: requiredLanguages }));
        methods.trigger();
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load apply-to-job prefill:", err);
        setLoadError(apiErrorMessageFormat(err, "Unable to load this candidate's information for this job."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attested, candidateId, jobId, jobTitleFromState]);

  useEffect(() => {
    if (!attested || !sourceApplicationId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await CandidateService.fetchCandidateResume(candidateId, sourceApplicationId);
        if (cancelled) return;

        const contentType = res?.headers?.["content-type"] || "";
        const contentDisposition = res?.headers?.["content-disposition"] || "";
        const blob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: contentType });
        const objectUrl = URL.createObjectURL(blob);

        const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        const asciiMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
        const filename = utf8Match?.[1] ? decodeURIComponent(utf8Match[1]) : asciiMatch?.[1] || (contentType.includes("pdf") ? "resume.pdf" : "resume.docx");

        setResumeFile((prev) => {
          if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
          return { filename, objectUrl, blob, type: contentType };
        });
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load resume preview:", err);
        toast.error(apiErrorMessageFormat(err, "Failed to load resume preview."));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attested, candidateId, sourceApplicationId]);

  useEffect(
    () => () => {
      setResumeFile((prev) => {
        if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
        return null;
      });
    },
    [],
  );

  const handleDownloadResume = useCallback(async () => {
    if (!candidateId || !sourceApplicationId) return;
    try {
      await CandidateService.downloadCandidateResume(candidateId, sourceApplicationId);
    } catch (err) {
      console.error("downloadResume error:", err);
      toast.error(apiErrorMessageFormat(err, "Failed to download resume."));
    }
  }, [candidateId, sourceApplicationId]);

  const scrollToSection = useCallback((key) => {
    const el = sectionRefs.current[key];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedSection(key);
    setTimeout(() => setHighlightedSection((current) => (current === key ? null : current)), 2000);
  }, []);

  const leaveToJobFit = useCallback(() => navigate(fromPath), [navigate, fromPath]);

  const buildJobFitApplyPayload = useCallback(
    (formData) => ({
      draft_data: {
        personal_information: {
          full_name: formData.personal_information.name,
          email: formData.personal_information.email,
          phone: formData.personal_information.phone,
          location: formData.personal_information.location,
          website: websiteValue(formData.personal_information.portfolio),
          linkedin: formData.personal_information.linkedin,
        },
        summary: formData.personal_information.summary,
        skills: formData.skills.list,
        languages: formData.skills.languages,
        education: formData.education,
        work_experience: formData.experience.map((exp) => ({
          title: exp.jobTitle,
          company: exp.company,
          location: exp.location,
          start_date: exp.startDate,
          end_date: exp.endDate,
          description: exp.description,
          achievements: exp.achievements,
        })),
        additional_sections: (formData.additionalSections || []).flatMap((sec) =>
          (sec.items || [])
            .map((entry) => ({
              section_title: sec.section_title,
              fields: Object.fromEntries((Array.isArray(entry) ? entry : [entry]).filter((i) => i?.key?.trim() && i?.value?.trim()).map((i) => [i.key, i.value])),
            }))
            .filter((s) => Object.keys(s.fields).length > 0),
        ),
      },
      current_salary: emptyToNull(formData.compensation?.current_salary),
      expected_salary: emptyToNull(formData.compensation?.expected_salary),
      notice_period: formData.compensation?.notice_period || null,
      total_experience: emptyToNull(formData.compensation?.total_experience),
      instagram_url: formData.social?.instagram_url || null,
      facebook_url: formData.social?.facebook_url || null,
      twitter_url: formData.social?.twitter_url || null,
      // Reaching this page at all required ticking the attestation dialog on
      // the Suitable Jobs card (guarded on mount above) — this is that same
      // attestation, carried through to the write the server records it
      // against.
      attestation: true,
    }),
    [],
  );

  const onSubmit = methods.handleSubmit(
    // ── Success ──
    async (formData) => {
      const payload = buildJobFitApplyPayload(formData);
      setSubmitting(true);
      try {
        const res = await CandidateService.submitJobFitApply(candidateId, jobId, payload);

        if (res?.already_applied) {
          toast.success(res?.message || "This candidate has already applied to this job.");
        } else if (res?.email_confirmation_required !== false) {
          toast.success(res?.email_masked ? `${res.message} (${res.email_masked})` : res?.message || "Consent email sent to the candidate.");
        } else {
          toast.success(res?.message || "Application submitted.");
        }

        navigate(fromPath, { state: { fromApply: true } });
      } catch (err) {
        console.error("Job-fit apply submit failed:", err);
        toast.error(apiErrorMessageFormat(err, "Failed to submit the application."));
      } finally {
        setSubmitting(false);
      }
    },

    // ── Error: runs when validation fails ──
    (errors) => {
      const messages = [];
      const collect = (obj) => {
        for (const [, val] of Object.entries(obj)) {
          if (val?.message) messages.push(val.message);
          else if (val && typeof val === "object") collect(val);
        }
      };
      collect(errors);

      toast.error(messages.length > 0 ? messages[0] : "Please fix the errors above.", { duration: 4000 });

      const firstPath = getFirstErrorPath(errors);
      if (firstPath) {
        const el = document.querySelector(`[data-name="${firstPath}"]`) ?? document.querySelector(`[name="${firstPath}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        else scrollToSection(FIELD_TO_SECTION[firstPath.split(".")[0]]);
      }
    },
  );

  const sectionCardClass = (key) =>
    `bg-background-secondary rounded-xl border p-5 transition-all duration-500 ${
      highlightedSection === key ? "border-blue-400 shadow-[0_0_0_3px_rgba(96,165,250,0.3)]" : "border-border-primary"
    }`;

  if (!attested) {
    // Reached without going through the attestation dialog — send back
    // rather than asking a second time in a different shape.
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-20 text-center">
        <AlertTriangle className="mb-3 text-warning" size={32} aria-hidden="true" />
        <h1 className="text-lg font-semibold text-text-primary">Attestation required</h1>
        <p className="mt-2 text-sm text-text-secondary">
          Start this application from the Suitable Jobs tab's Apply button, which walks through the required attestation first.
        </p>
        <Button className="mt-5 rounded-lg bg-button-secondary px-4 py-2 text-sm text-text-base" onClick={() => navigate(fromPath)}>
          Back to Candidate
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin text-text-secondary" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-20 text-center">
        <AlertTriangle className="mb-3 text-warning" size={32} aria-hidden="true" />
        <h1 className="text-lg font-semibold text-text-primary">Unable to load this application</h1>
        <p className="mt-2 text-sm text-text-secondary">{loadError}</p>
        <Button className="mt-5 rounded-lg bg-button-secondary px-4 py-2 text-sm text-text-base" onClick={leaveToJobFit}>
          Back to Candidate
        </Button>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <SplitReviewLayout
        onBack={leaveToJobFit}
        backLabel="Back to candidate"
        backDisabled={submitting}
        left={
          <ReviewFormPane
            sections={SECTIONS}
            onSectionClick={scrollToSection}
            footer={
              <div className="flex flex-wrap items-center gap-2">
                <ReviewStatusChip label={saveStatus.label} tone={saveStatus.tone} />
                <Button onClick={onSubmit} disabled={submitting} className={cn(TOOLBAR_BUTTON, "ml-auto bg-button-secondary text-text-base")}>
                  {submitting ? "Submitting…" : "Confirm"}
                </Button>
              </div>
            }
          >
            <Card ref={(el) => (sectionRefs.current.personal = el)} className={sectionCardClass("personal")}>
              <StepPersonal mandatoryFields={mandatoryFields} />
            </Card>

            <Card ref={(el) => (sectionRefs.current.skills = el)} className={sectionCardClass("skills")}>
              <StepSkills
                mandatoryFields={mandatoryFields}
                languagesSlot={(disabled, required) => (
                  <LanguageProficiencyRows
                    name="skills.languages"
                    disabled={disabled}
                    emptyText="No languages added."
                    allowUnknownProficiency
                    lockedLanguages={requiredLanguageNames(jobRequiredLanguages)}
                    languageRequired={required?.language}
                    levelledLanguages={jobRequiredLanguages}
                  />
                )}
              />
            </Card>

            <Card ref={(el) => (sectionRefs.current.experience = el)} className={sectionCardClass("experience")}>
              <StepExperience mandatoryFields={mandatoryFields} autoCalculate={false} />
            </Card>

            <Card ref={(el) => (sectionRefs.current.education = el)} className={sectionCardClass("education")}>
              <StepEducation mandatoryFields={mandatoryFields} />
            </Card>

            <Card ref={(el) => (sectionRefs.current.salary = el)} className={sectionCardClass("salary")}>
              <StepSalary mandatoryFields={mandatoryFields} autoCalculate={false} currencySign={currencySign} />
            </Card>

            <Card ref={(el) => (sectionRefs.current.moreinfo = el)} className={sectionCardClass("moreinfo")}>
              <StepMoreInfo showSocials={false} mandatoryFields={mandatoryFields} />
            </Card>
          </ReviewFormPane>
        }
        right={<ResumePreview file={resumeFile} onDownload={handleDownloadResume} />}
      />
    </FormProvider>
  );
};

export default ApplyToJob;
